import React, { Component } from 'react';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Field, Form as FinalForm, FormRenderProps } from 'react-final-form';
import { Button, Grid } from 'semantic-ui-react';
import TextArea from 'components/final-form/TextArea';
import { UpsertTextDto } from 'ts-types/api.types';
import Input from 'components/final-form/Input';
import { required } from 'utils/validatorUtils';
import { addText, editText } from 'services/textServices';
import axios, { CancelTokenSource } from 'axios';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import FormContainer from 'components/layout/FormContainer';
import FormContent from 'components/layout/FormContent';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';

interface Props extends MessageSourceProps {
  toggleAddEditModal: () => void,
  onCancel: () => void,
  onSave: () => void,
  textId?: number,
  text?: UpsertTextDto
}

interface State {
  errorMessage?: string,
  cancelTokenSource: CancelTokenSource
}

class UpsertTextForm extends Component<Props, State> {

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      cancelTokenSource
    }
  }

  handleSubmit = async (values: Partial<UpsertTextDto>) => {
    let saveValues: Partial<UpsertTextDto> = {...values};

    const {textId, getMessage, onSave} = this.props;
    const {cancelTokenSource} = this.state;

    const handleError = () => {
      this.setState({
        errorMessage: getMessage("error.general")
      })
    };

    if (textId) {
      await editText(textId, saveValues, cancelTokenSource)
          .then(onSave)
          .catch(handleError)
    } else {
      await addText(saveValues, cancelTokenSource)
          .then(onSave)
          .catch(handleError)
    }
  };

  setErrorMessage = (errorMessage?: string) => {
    this.setState({
      errorMessage: errorMessage
    })
  };

  render(): React.ReactNode {

    const {text} = this.props;

    return (
        <FinalForm
            onSubmit={this.handleSubmit}
            initialValues={text}
            subscription={{values: true, pristine: true, submitting: true}}
            render={this.renderForm}
        />
    );
  }

  renderForm = ({handleSubmit, submitting}: FormRenderProps): React.ReactNode => {

    const {getMessage, onCancel} = this.props;
    const {errorMessage} = this.state;

    return (
        <form onSubmit={handleSubmit}>
          <FormContainer>
            <FormContent>
                {errorMessage
                && (
                    <StyledErrorMessage  onDismiss={() => this.setErrorMessage()}>
                      {errorMessage}
                    </StyledErrorMessage>
                )
                }
                <PraxFormGrid>
                  <Grid.Row>
                    <Grid.Column width={5} verticalAlign="middle">
                      {getMessage("text.description")}
                    </Grid.Column>
                    <Grid.Column width={11} verticalAlign="middle">
                      <Field
                          fluid
                          name="description"
                          component={Input}
                          validate={required}
                          autoFocus
                      />
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column>
                      {getMessage("text.text")}
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column>
                      <Field name="text"
                             component={TextArea}
                      />
                    </Grid.Column>
                  </Grid.Row>
                </PraxFormGrid>
            </FormContent>

            <FormButtonsRow>
              <Button compact type="submit" primary disabled={submitting}>{getMessage("button.save")}</Button>
              <Button compact type="button" disabled={submitting}
                      onClick={onCancel}>{getMessage("button.cancel")}</Button>
            </FormButtonsRow>
          </FormContainer>
        </form>
    );
  };

}

export default withMessages(UpsertTextForm);
